import {useState} from "react";
import {useDisclosure, Spinner, Tag, TagLabel, VStack} from "@chakra-ui/react";
import {
    Chat,
    InfoBoxProps,
    Participant,
    Request,
    RequestAction,
    RequestVerification,
    requestInfos,
    Structure
} from "../../models";
import {
    ConfirmationButton,
	Description,
	EmptyState,
	InfoBox,
	L5Button,
	StructureDrawer,
	UserCard
} from  "../UI";
import {scrollbar} from "../styling";
import {requestMessages} from "../../helper/utils";

const statuses: Request["status"][] = ["approved", "cancelled", "declined"];
type Props = {
    canCancel?: boolean
    chat?: Chat
    checkStatus: (v: RequestVerification) => boolean
    currentEmail: string
    loading?: boolean
    onAction: (v: RequestAction) => void
    request?: Request
}

function Participants(p: {users: Participant[], approver: string}) {
    const isApprover = (v: string) => v === p.approver;
    return <VStack
        alignItems="stretch"
        flex="2 1 auto"
        maxHeight="100dvb"
        overflowY="auto"
        sx={scrollbar.styled}
        gap={4}
        py="1rem"
        bgColor="#F5F7F9"
        borderRadius="8px"
        p=".5rem .75rem"
        color="dark.500"
        w="100%"
    >
        {p.users.map((u) => <UserCard
            key={u.data.email}
            title={u.type === "onboarded" ? u.data.name : u.data.email.split("@")[0]}
            subtitle={u.data.email}
            url={u.type === "onboarded" ?  u.data.profileImage : undefined}
        >
            {isApprover(u.data.email) && <Tag color="primary.500" borderColor="primary.500" variant="outline" borderRadius={5}>
                <TagLabel>Approver</TagLabel>
            </Tag>}
        </UserCard>)}
    </VStack>;
}

export function RequestContent(p: Props) {
    const infos: InfoBoxProps[] = [];
    const [structure, setStructure] = useState<Structure|null>(null);
    const drawer = useDisclosure();
    if (p.request) {
        infos.push(requestInfos(p.request));
    }
    if (p.chat && p.request) {
        infos.push({
            flex: "2 1 auto",
            icon: "s_users",
            items: [],
            node: <Participants
                approver={p.request.approver}
                users={p.request.participants} />,
            maxHeight: "100%",
            overflowY: "hidden",
            title: "participants"
        });
    }
    if (infos.length <= 0) {
        return <EmptyState
            title="No Request Selected"
            desc={"You did not selected a request to view its details. If you" +
            " believe this is an error, please contact support for assistance."}
            boxShadow="none"
        />;
    }
    function handleOpen(type: "reassign"|"resolve") {
        if (!p.checkStatus({type: "identity-verified"})) {
            return;
        }
        if (!p.checkStatus({type: "biometric-setup"})) {
            return;
        }
        setStructure({data: {onAction: p.onAction}, type});
        drawer.onOpen();
    }
    function handleClose() {
        setStructure(null);
        drawer.onClose();
    }
    return <>
        <StructureDrawer
            isOpen={drawer.isOpen}
            onClose={handleClose}
            structure={structure}
        />
        <VStack
            flex="2 1 58dvb"
            overflowY="auto"
            overflowX="clip"
            align="stretch"
            maxHeight="100%"
            sx={scrollbar.styled}
        >
            {infos.map((info, idx) => <InfoBox key={idx} {...info}/>)}
        </VStack>
        {p.request && (!statuses.includes(p.request.status)) &&  <>
            <L5Button
                type="primary"
                onClick={() => handleOpen("resolve")}
                isDisabled={
                    (p.request?.approver !== p.currentEmail) || p.loading
                }
            >
                {p.loading ? <Spinner/> : "Resolve"}
            </L5Button>
            <L5Button
                type="secondary"
                onClick={() => handleOpen("reassign")}
                isDisabled={
                    (p.request?.approver !== p.currentEmail) || p.loading
                }
            >
                {p.loading ? <Spinner/> : "Change Resolver"}
            </L5Button>
            <ConfirmationButton
                buttonType="secondary"
                buttonLabel="Cancel"
                cancelLabel="Keep Request Open"
                description={<Description {...requestMessages.cancellation}/>}
                modalTitle=""
                onSubmit={() => p.onAction({type: "cancel"})}
                isDisabled={!p.canCancel}
                submitLabel="Cancel Request"
            />
        </>
        }
    </>
}
